import React, { useMemo } from 'react';

import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomUTMReportPDF from './custom-utm-report';
import CustomUTMBreakdown from './custom-utm-breakdown';
import CustomVisualInspectionSummary from './custom-utm-summary';
import CustomDamageClass from '../custom-general/custom-damage-class';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomUTMAnomaly from './customUTMAnomaly';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import GeneralDrawing from '../custom-visual-inspection/general-drawing';
import OverviewSheet from '../custom-general/overview-sheet';
import PID from '../custom-visual-inspection/pid';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface CustomExxonMobilUTMStructureReportProps {
	coverData: models.CustomFVIData;
    reportTitle: string;
    summary: models.CustomUTMStructureSummary;
    anomalyItems: models.NewAnomalyItem[];
    pidData: models.PIDData;
	generalDrawing: models.PDFImageProps[];
	photographicData: models.PDFImageProps[];
	projectDetails: models.Project;
	isoImage: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	inspectionOverview: models.CustomUTMOverview;
}

const CustomExxonMobilUTMStructureReport: React.FC<CustomExxonMobilUTMStructureReportProps> = (props): JSX.Element => {
	const { inspectionTypeName } = props.projectDetails;
    
	const overview = useCoverSheetOverview();

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const anomalyHeaders = useMemo(() => {
		return [
			{
				label: 'Part Name',
				value: props.summary.itemName,
			},
		];
	}, [props.summary.itemName]);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

            <CustomInfoSheet
				projectDetails={props.projectDetails}
                workOrderNumber={props.coverData.workOrderNumber}
                reportTitle={props.reportTitle}
            />

            <OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<CustomDamageClass />

			<ExecutiveSummary summary={props.summary.overview} />

			<CustomUTMBreakdown
				corrosionAllowance={props.inspectionOverview.corrosionAllowance}
				inspectionItems={props.utInspectionItems} 
			/>

			<CustomVisualInspectionSummary anomalyItems={props.anomalyItems} />

			<PID
				hasPID={props.pidData.hasPID}
				pidImages={props.pidData.pIDImages}
				pidNumber={props.pidData.pIDNumber}
				sectionTitle="P&ID"
			/>

			<GeneralDrawing generalDrawing={props.generalDrawing} />

           <CustomUTMAnomaly
		   		lineNumberID={1}
                anomalyItems={props.anomalyItems} 
				headers={anomalyHeaders}
            />

			<CustomUTMReportPDF
				tmlLocations={props.isoImage}
				utInspectionItems={props.utInspectionItems}
				inspectionOverview={props.inspectionOverview}
			/>

			<CustomGeneralPhotos label="Photographic Record" photos={props.photographicData} />
		</React.Fragment>
	);
};

export default withPDF(CustomExxonMobilUTMStructureReport);
